import { useState, useMemo, useRef, useEffect } from 'react'
import { Search, Filter, RefreshCw, ChevronRight, Home, Calendar, ArrowRight, Clock, Trash2, CheckSquare, Square, ListChecks, Wrench, Package, AlertTriangle } from 'lucide-react'
import SyncBadge from '../components/ui/SyncBadge'
import { useAppStore } from '../store/useAppStore'
import { usePendingMrcStore } from '../store/usePendingMrcStore'
import { OLYMPIC_AGENCIES } from '../utils/agencies'
import ConfirmAgentModal from '../components/ConfirmAgentModal'
import Card from '../components/ui/Card'
import api from '../services/api'
import type { EdlDraft } from '../store/useEdlDraftStore'
import { useSyncStatus } from '../store/useSyncStatus'

// ── Version de l'application ─────────────────────────────────────────────
import { APP_VERSION } from '../version'

function fmtDateShort(d: string | null | undefined): string | null {
  if (!d) return null
  try {
    return new Date(d).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit'
    })
  } catch { return null }
}

export default function ContractListScreen() {
  const { contracts, setContracts, selectedAgency, setStep, lastSync, logout, credentials } = useAppStore()
  const [search,          setSearch]          = useState('')
  const [agencyFilter,    setAgencyFilter]    = useState(selectedAgency || '')
  const [showFilter,      setShowFilter]      = useState(false)
  const [pendingContract, setPendingContract] = useState<any>(null)
  const [drafts,          setDrafts]          = useState<EdlDraft[]>([])
  const uploadQueue = useAppStore(s => s.uploadQueue)
  const queueCount  = uploadQueue.length
  const hasFailed   = uploadQueue.some((i: any) => i.status === 'failed')
  const selectionQueue       = useAppStore(s => s.selectionQueue) as any[]
  const addToSelectionQueue   = useAppStore(s => s.addToSelectionQueue)
  const removeFromSelectionQueue = useAppStore(s => s.removeFromSelectionQueue)
  const markSelectionCacheReady  = useAppStore(s => s.markSelectionCacheReady)
  const [selectionMode, setSelectionMode] = useState(false)
  const [preparingBatch, setPreparingBatch] = useState(false)
  const [deleteConfirmKey, setDeleteConfirmKey] = useState<string | null>(null)
  const isSyncing = useSyncStatus(s => s.isSyncing)
  const enrichedRef = useRef<any>(null)
  
  const pendingReturns = usePendingMrcStore(s => s.pendingReturns)
  const fetchGlobalAlerts = usePendingMrcStore(s => s.fetchGlobalAlerts)

  // ✅ Filtrer les alertes MRC par agence active
  // pendingReturns.agencyCode est un code string (ex: "09")
  // agencyFilter est un id agence (ex: "121" = Plombières)
  // Si aucun filtre agence : afficher toutes les alertes
  // Si un filtre agence actif : afficher seulement les alertes de cette agence
  const activeAgencyCode = agencyFilter
    ? OLYMPIC_AGENCIES.find(a => a.id === agencyFilter)?.code ?? null
    : null

  const filteredPendingReturns = activeAgencyCode
    ? pendingReturns.filter(r => r.agencyCode === activeAgencyCode)
    : pendingReturns

  // ✅ Fetch silencieux des alertes MRC globales au mount
  // ✅ FIX — Passer les IDs actifs pour auto-nettoyer les alertes périmées (contrat clôturé MRC)
  useEffect(() => {
    const agencyCodes = [...new Set(
      contracts.map((c: any) => c.agenceDepartCode).filter(Boolean)
    )] as string[];
    const activeIds = contracts.map((c: any) => String(c.rawId ?? c.id));
    if (agencyCodes.length > 0) {
      fetchGlobalAlerts(agencyCodes, activeIds);
    }
  }, [contracts, fetchGlobalAlerts])


  // ✅ Charger les brouillons au démarrage
  const loadDrafts = async () => {
    try {
      const { edlDraftStore } = await import('../store/useEdlDraftStore')
      const allDrafts = await edlDraftStore.getAll()
      setDrafts(allDrafts)
    } catch(e: any) { console.warn('Erreur chargement brouillons', e) }
  }

  // ✅ Enrichissement en masse au chargement
  useEffect(() => {
    const controller = new AbortController()
    
    const enrichAll = async () => {
      const toEnrich = (contracts || []).filter((c: any) => !c._enriched && (!c.startDate || !c.endDate))
      if (!toEnrich.length) return
      
      const chunks = []
      for (let i = 0; i < Math.min(toEnrich.length, 20); i += 5) {
        chunks.push(toEnrich.slice(i, i + 5))
      }
      
      let updated = [...contracts]
      for (const chunk of chunks) {
        if (controller.signal.aborted) break
        
        await Promise.all(chunk.map(async (contract: any) => {
          try {
            const url = `/api/contracts/${contract.id}/details?typeDocument=${contract.typeDocument || 'CL'}`
            const res = await api.get(url, { signal: controller.signal })
            
            const data = res.data
            if (data && data.id && (data.startDate || data.endDate)) {
              const idx = updated.findIndex((c: any) => c.id === contract.id)
              if (idx !== -1) updated[idx] = { ...updated[idx], ...data, _enriched: true }
            }
          } catch(e: any) {}
        }))
        
        if (!controller.signal.aborted) {
          useAppStore.getState().setContracts?.([...updated])
        }
      }
    }
    enrichAll()
    
    return () => {
      controller.abort()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSync])

  // ✅ FIX v2.2 — Recharger les brouillons quand on revient de sync
  useEffect(() => { loadDrafts() }, [lastSync])

  const filtered = useMemo(() => {
    let list: any[] = contracts || []
    if (agencyFilter) {
      list = list.filter((c: any) => String(c.agenceDepartId) === String(agencyFilter))
    }
    if (search.trim()) {
      const s = search.toUpperCase().replace(/[-\s]/g, '')
      list = list.filter((c: any) =>
        c.plate?.toUpperCase().replace(/[-\s]/g, '').includes(s) ||
        c.clientName?.toUpperCase().includes(s) ||
        c.numeroDocument?.toString().includes(s)
      )
    }
    // ✅ Tri : contrats les plus récents en premier (par startDate desc)
    // Les contrats sans date sont poussés en fin de liste
    list = [...list].sort((a: any, b: any) => {
      const da = a.startDate ? new Date(a.startDate).getTime() : 0
      const db = b.startDate ? new Date(b.startDate).getTime() : 0
      return db - da
    })
    return list
  }, [contracts, search, agencyFilter])

  const handleSelect = async (contract: any) => {
    let enriched = { ...contract }
    if (!contract._enriched) {
      try {
        const url = `/api/contracts/${contract.id}/details?typeDocument=${contract.typeDocument || 'CL'}`
        const res = await api.get(url)
        const data = res.data
        if (data && data.id) enriched = { ...contract, ...data, _enriched: true }
      } catch(e: any) {
        console.warn('Enrichissement contrat échoué:', e.message)
      }
    }
    enrichedRef.current = enriched
    setPendingContract(enriched)

    // ✅ Pré-téléchargement background des photos départ (fire-and-forget)
    // Permet d'avoir les photos en cache avant ChooseScreen, même offline
    const cid = String(enriched.rawId ?? enriched.id)
    import('../store/offlinePhotoStore').then(async ({ offlinePhotoStore }) => {
      try {
        const cached = await offlinePhotoStore.getDepartureCache(cid)
        if (cached && Object.keys(cached).length > 0) {
          console.log(`[ContractList] Photos départ déjà en cache pour ${cid}`)
          return
        }
        // ✅ FIX — Utiliser l'endpoint Supabase (format {zone: url}) et non MRC (format [{id, libelle}])
        const res = await api.get(`/api/edl/${cid}/photos?type=depart`)
        if (res.data?.photos && typeof res.data.photos === 'object' && !Array.isArray(res.data.photos)
            && Object.keys(res.data.photos).length > 0) {
          await offlinePhotoStore.saveDepartureCache(cid, res.data.photos)
          console.log(`[ContractList] ✅ Photos départ pré-chargées pour ${cid}:`, Object.keys(res.data.photos).length)
        }
      } catch (err: any) {
        // 404 = pas d'EDL départ → normal, silencieux
        if (err?.response?.status !== 404) {
          console.warn(`[ContractList] Pré-chargement photos départ ${cid}:`, err?.message)
        }
      }
    }).catch(() => {})
  }

  const handleConfirmed = () => {
    const contractToStart = enrichedRef.current || pendingContract
    setPendingContract(null)
    enrichedRef.current = null
    // ✅ FIX: Ne PAS appeler startInspection ici — le type n'est pas encore choisi.
    // On stocke seulement le contrat en mémoire pour que ChooseScreen y accède.
    useAppStore.setState((state: any) => ({
      inspection: {
        ...state.inspection,
        contract: contractToStart,
        type: null,           // type sera choisi dans ChooseScreen
      },
      step: 'choose',
    }))
  }

  const handleResumeDraft = async (draft: EdlDraft) => {
    // Si c'est un retour, on tente de recharger les photos départ (cache-first)
    if (draft.type === 'retour' && (draft as any).contract?.id) {
      try {
        const { offlinePhotoStore } = await import('../store/offlinePhotoStore')
        const contractId = (draft as any).contract.rawId ?? (draft as any).contract.id

        // 1. Tenter le cache local
        const cached = await offlinePhotoStore.getDepartureCache(contractId)
        if (cached && Object.keys(cached).length > 0) {
          const { photoMemory } = await import('../store/photoMemoryStore')
          photoMemory.setDepartPhotosFromUrls(cached, contractId)
          console.log('[ContractListScreen] Photos départ depuis cache local:', Object.keys(cached).length)
        } else {
          // 2. Fallback réseau + mise en cache
          // ✅ FIX — Utiliser l'endpoint Supabase (format {zone: url})
          const res = await api.get(`/api/edl/${contractId}/photos?type=depart`)
          const data = res.data
          if (data?.photos && typeof data.photos === 'object' && !Array.isArray(data.photos)
              && Object.keys(data.photos).length > 0) {
            const { photoMemory } = await import('../store/photoMemoryStore')
            photoMemory.setDepartPhotosFromUrls(data.photos, contractId)
            await offlinePhotoStore.saveDepartureCache(contractId, data.photos)
            console.log('[ContractListScreen] Photos départ depuis réseau + cache:', Object.keys(data.photos).length)
          }
        }
      } catch (err: any) {
        console.warn('Erreur chargement photos départ (resume draft):', err?.message)
      }
    }
    useAppStore.getState().restoreFromDraft(draft)
  }

  const handleDeleteDraft = async (draft: EdlDraft) => {
    setDeleteConfirmKey(null)
    try {
      // Utiliser draft.key (inspectionKey v5.0) comme clé primaire IndexedDB
      const idbKey = draft.key || draft.contractId
      console.log('[handleDeleteDraft] Suppression clé:', idbKey, 'type:', draft.type)
      
      const { offlinePhotoStore } = await import('../store/offlinePhotoStore')
      await offlinePhotoStore.clearDraft(idbKey, draft.type)
      await offlinePhotoStore.clearPhotos(idbKey, draft.type)
      console.log('[handleDeleteDraft] ✅ Brouillon supprimé:', idbKey)

      // ✅ Éviter la résurrection du brouillon par autoSaveDraft
      const state = useAppStore.getState()
      const inMemoryId = state.inspection?.contract?.rawId ?? state.inspection?.contract?.id
      if (inMemoryId === draft.contractId && state.inspection?.type === draft.type) {
        useAppStore.setState({
          inspection: {
            contract: null, type: null, damages: [], mileage: null, fuel: null,
            signatureAgent: null, signatureClient: null, completedAt: null,
            pendingPhotoSlot: null, pendingPhotoData: null, photosChecked: false,
            accidentDeclaration: null, startedAt: null, deviceInfo: null, securityCheck: null
          }
        })
        import('../store/photoMemoryStore').then(m => m.photoMemory.clear())
      }

      await loadDrafts()
    } catch(e) {
      console.error('Erreur suppression brouillon', e)
    }
  }

  const syncLabel = lastSync
    ? 'Sync · ' + new Date(lastSync).toLocaleString('fr-FR', {
        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
      })
    : 'Sync'

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">

      {pendingContract && (
        <ConfirmAgentModal
          contract={pendingContract}
          inspectionType="depart"
          onConfirm={handleConfirmed}
          onCancel={() => { setPendingContract(null); enrichedRef.current = null }}
        />
      )}

      <div className="sticky top-0 z-10 bg-white shadow-sm px-4 pt-4 pb-3">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-lg font-black text-gray-800">Contrats</h1>
            <p className="text-xs text-gray-400">{contracts.length} contrats · {syncLabel}</p>
          </div>

          {/* ── Boutons header ── */}
<div className="flex gap-2 items-center">
  {/* Bouton "Prépas disponibles" — Phase 3 */}
  <button onClick={() => setStep('prep-link')}
    className="p-2 rounded-xl border border-purple-200 bg-white text-purple-600 hover:bg-purple-50 transition-colors"
    title="Préparations disponibles">
    <Package size={18}/>
  </button>
  {/* Bouton "Inspection libre" — Phase 3 */}
  <button onClick={() => setStep('prep-inspect')}
    className="p-2 rounded-xl border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
    title="Inspection libre (sans contrat)">
    <Wrench size={18}/>
  </button>
  {/* Bouton "Ma Sélection" — visible si des contrats sont sélectionnés */}
  {selectionQueue.length > 0 && (
    <button onClick={() => setStep('selection')}
      className="relative p-2 rounded-xl border border-green-300 bg-green-50 text-green-700"
      title="Ma Sélection">
      <ListChecks size={18}/>
      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white bg-green-600">
        {selectionQueue.length}
      </span>
    </button>
  )}
  {/* Bouton mode sélection multiple */}
  <button onClick={() => setSelectionMode(v => !v)}
    className={`p-2 rounded-xl border text-sm font-semibold transition-all ${selectionMode ? 'bg-green-500 text-white border-green-500' : 'bg-white border-gray-200 text-gray-600'}`}
    title="Mode sélection">
    <CheckSquare size={18}/>
  </button>
  <button onClick={() => setStep('queue')}
    className={`relative p-2 rounded-xl border transition-all ${
      isSyncing
        ? 'border-blue-400 bg-blue-50 text-blue-600'
        : 'border-gray-200 bg-white text-gray-600'
    }`}
    title="File d'attente">
    {isSyncing ? (
      <RefreshCw size={18} className="animate-spin"/>
    ) : (
      <Clock size={18}/>
    )}
    {queueCount > 0 && (
      <span className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white ${
        hasFailed ? 'bg-red-500 animate-pulse' : 'bg-orange-500'
      }`}>
        {queueCount}
      </span>
    )}
  </button>
  <button onClick={() => setShowFilter(v => !v)}
    className={`p-2 rounded-xl border text-sm font-semibold transition-all ${showFilter ? 'bg-blue-500 text-white border-blue-500' : 'bg-white border-gray-200 text-gray-600'}`}>
    <Filter size={18}/>
  </button>
  <button onClick={() => setStep('sync')}
    className="p-2 rounded-xl border border-gray-200 bg-white text-gray-600" title={syncLabel}>
    <RefreshCw size={18}/>
  </button>
  {/* ✅ SyncBadge — brouillons + photos offline + queue */}
  <SyncBadge />
  <button onClick={() => setStep('home')}
    className="p-2 rounded-xl border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors" title="Accueil">
    <Home size={18}/>
  </button>
</div>

        </div>

        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input type="text" placeholder="Rechercher une plaque, client..."
            value={search} onChange={e => setSearch(e.target.value)}
            autoCapitalize="characters"
            className="w-full pl-9 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-gray-50"/>
          {search && (
            <button onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
          )}
        </div>

        {showFilter && (
          <div className="mt-2 flex gap-2 flex-wrap">
            {OLYMPIC_AGENCIES.map(ag => (
              <button key={ag.id}
                onClick={() => setAgencyFilter((prev: string) => prev === ag.id ? '' : ag.id)}
                className={`px-2 py-1 rounded-full text-xs font-semibold border flex items-center gap-1 ${
                  agencyFilter === ag.id ? 'text-white border-transparent' : 'bg-white border-gray-200 text-gray-600'
                }`}
                style={agencyFilter === ag.id ? { backgroundColor: ag.color, borderColor: ag.color } : {}}>
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: ag.color }}/>
                {ag.code} {ag.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {filteredPendingReturns.length > 0 && (
        <div className="px-4 mt-3">
          <button
            onClick={() => setStep('pending-mrc')}
            className="w-full flex items-center justify-between bg-red-500 hover:bg-red-600 text-white p-3 rounded-xl shadow-sm transition-colors active:scale-[0.98]"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle size={20} className="text-red-100" />
              <div className="text-left">
                <p className="font-bold text-sm">Action requise dans MyRentCar</p>
                <p className="text-xs text-red-100">
                  {filteredPendingReturns.length} retour{filteredPendingReturns.length > 1 ? 's' : ''} en attente de saisie
                  {activeAgencyCode && ` — agence ${activeAgencyCode}`}
                </p>
              </div>
            </div>
            <ChevronRight size={20} className="text-red-200" />
          </button>
        </div>
      )}

      <div className="px-4 pt-2 pb-1">
        <p className="text-xs text-gray-400">
          {filtered.length} résultat{filtered.length !== 1 ? 's' : ''}
          {search && <span> pour "<strong>{search}</strong>"</span>}
        </p>
      </div>

      <div className="flex-1 px-4 pb-2 flex flex-col gap-2">
        {filtered.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <p className="text-4xl mb-2">🔍</p>
            <p className="font-semibold">Aucun contrat trouvé</p>
            <p className="text-sm mt-1">Essayez avec la plaque ou le n° de contrat</p>
          </div>
        )}

        {(() => {
          const uploadQueue = useAppStore.getState().uploadQueue || []
          const pendingContractIds = new Set(uploadQueue.map((i: any) => i.contractId))
          const visibleDrafts = drafts.filter(d => !pendingContractIds.has(d.contractId))
          
          if (visibleDrafts.length === 0 || search) return null
          
          return (
            <div className="mb-4 text-left">
              <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-2 px-1">
                Brouillons en cours ({visibleDrafts.length})
              </h2>
              <div className="flex flex-col gap-2">
                {visibleDrafts.map(draft => (
                  <Card key={draft.key} className="px-4 py-3 border-l-4 border-orange-400 bg-orange-50/50">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-900">{draft.plate || draft.contractId}</span>
                          <span className="text-[10px] font-bold uppercase tracking-wider text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded">
                            {draft.type}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Étape : <span className="font-semibold">{draft.step}</span> · 
                          {draft.savedAt ? ' Sauvegardé le ' + new Date(draft.savedAt).toLocaleDateString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      {deleteConfirmKey === draft.key ? (
                        // ── Mode confirmation inline ─────────────────────────
                        <>
                          <span className="flex-1 text-xs text-red-700 font-semibold">Supprimer définitivement ?</span>
                          <button
                            onClick={() => handleDeleteDraft(draft)}
                            className="px-3 py-2 bg-red-500 text-white text-xs font-bold rounded-lg active:scale-95 transition-transform"
                          >
                            Supprimer
                          </button>
                          <button
                            onClick={() => setDeleteConfirmKey(null)}
                            className="px-3 py-2 bg-gray-100 text-gray-600 text-xs font-semibold rounded-lg active:scale-95 transition-transform"
                          >
                            Annuler
                          </button>
                        </>
                      ) : (
                        // ── Mode normal ─────────────────────────────────────
                        <>
                          <button 
                            onClick={() => handleResumeDraft(draft)}
                            className="flex-1 bg-orange-500 text-white text-xs font-semibold py-2 rounded-lg hover:bg-orange-600 transition-colors"
                          >
                            Reprendre
                          </button>
                          <button 
                            onClick={() => setDeleteConfirmKey(draft.key)}
                            className="p-2 border border-red-200 text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                            title="Supprimer ce brouillon"
                          >
                            <Trash2 size={16}/>
                          </button>
                        </>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )
        })()}
        {/* ── Liste des  Contrats ── */}
        {search || drafts.length === 0 ? null : (
           <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mt-2 mb-1 px-1">
             Contrats
           </h2>
        )}
        {filtered.map((contract: any) => {
          const contractId = String(contract.rawId ?? contract.id)
          const isSelected = selectionQueue.some((c: any) => String(c.rawId ?? c.id) === contractId)

          if (selectionMode) {
            return (
              <button
                key={contract.id}
                onClick={() => {
                  if (isSelected) {
                    removeFromSelectionQueue(contractId)
                  } else {
                    addToSelectionQueue(contract)
                  }
                }}
                className="text-left w-full"
              >
                <Card className={`px-4 py-3 transition-all active:scale-[0.98] ${
                  isSelected ? 'ring-2 ring-green-500 bg-green-50/50' : 'hover:shadow-md'
                }`}>
                  <div className="flex items-center gap-3">
                    {isSelected
                      ? <CheckSquare size={20} className="text-green-600 flex-shrink-0" />
                      : <Square size={20} className="text-gray-300 flex-shrink-0" />
                    }
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-black text-gray-900 text-base tracking-wide">{contract.plate}</span>
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{contract.vehicleName || '—'}</p>
                      <p className="text-sm font-semibold text-gray-700 truncate mt-0.5">{contract.clientName}</p>
                    </div>
                  </div>
                </Card>
              </button>
            )
          }

          return (
            <ContractCard key={contract.id} contract={contract} onClick={() => handleSelect(contract)} />
          )
        })}
      </div>

      {/* ── Barre flottante de sélection ── */}
      {selectionMode && selectionQueue.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg px-4 py-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {selectionQueue.length} véhicule{selectionQueue.length > 1 ? 's' : ''} sélectionné{selectionQueue.length > 1 ? 's' : ''}
            </span>
            <button
              onClick={async () => {
                setPreparingBatch(true)
                try {
                  const { offlinePhotoStore } = await import('../store/offlinePhotoStore')
                  for (const contract of selectionQueue as any[]) {
                    const cid = String(contract.rawId ?? contract.id)
                    try {
                      // Vérifier si déjà en cache
                      const cached = await offlinePhotoStore.getDepartureCache(cid)
                      if (cached) {
                        markSelectionCacheReady(cid)
                        continue
                      }
                      // Télécharger et mettre en cache
                      // ✅ FIX — Utiliser l'endpoint Supabase (format {zone: url})
                      const cid2 = String(contract.rawId ?? contract.id)
                      const res = await api.get(`/api/edl/${cid2}/photos?type=depart`)
                      if (res.data?.photos && typeof res.data.photos === 'object' && !Array.isArray(res.data.photos)
                          && Object.keys(res.data.photos).length > 0) {
                        await offlinePhotoStore.saveDepartureCache(cid, res.data.photos)
                        markSelectionCacheReady(cid)
                      }
                    } catch (err: any) {
                      // 404 = pas encore d'EDL départ pour ce contrat → normal, on continue
                      if (err?.response?.status === 404) {
                        console.log(`[Batch] ${cid}: pas de photos départ (EDL départ non effectué)`)
                      } else {
                        console.warn(`[Batch] Erreur pré-chargement ${cid}:`, err?.message)
                      }
                    }
                  }
                  setSelectionMode(false)
                  setStep('selection')
                } catch (err: any) {
                  console.error('[Batch] Erreur préparation:', err)
                } finally {
                  setPreparingBatch(false)
                }
              }}
              disabled={preparingBatch}
              className={`px-5 py-2.5 rounded-xl font-bold text-sm text-white transition-all ${
                preparingBatch ? 'bg-gray-400 cursor-wait' : 'bg-green-600 hover:bg-green-700 active:scale-95'
              }`}
            >
              {preparingBatch ? 'Préparation...' : `Préparer ${selectionQueue.length} retour${selectionQueue.length > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}

      {/* ── Footer version ── */}
      <div className="flex-shrink-0 pb-4 pt-2 flex justify-center items-center bg-gray-50">
        <span className="text-[10px] text-gray-400 font-mono tracking-wide select-none">
          v{APP_VERSION}
        </span>
      </div>

    </div>
  )
}

function ContractCard({ contract, onClick }: { contract: any; onClick: () => void }) {
  const ag = OLYMPIC_AGENCIES.find(a => String(a.id) === String(contract.agenceDepartId))
  const startFmt = fmtDateShort(contract.startDate)
  const endFmt   = fmtDateShort(contract.endDate)

  return (
    <button onClick={onClick} className="text-left w-full">
      <Card className="px-4 py-3 hover:shadow-md transition-all active:scale-[0.98]">
        <div className="flex items-center gap-3">
          <div className="w-1.5 self-stretch rounded-full flex-shrink-0"
            style={{ backgroundColor: ag?.color || '#e5e7eb' }}/>
          <div className="flex-1 min-w-0">
            {/* Ligne 1 : plaque + badge + n° contrat */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-black text-gray-900 text-base tracking-wide">{contract.plate}</span>
                {ag && (
                  <span className="text-xs font-bold px-1.5 py-0.5 rounded-md flex-shrink-0"
                    style={{ backgroundColor: ag.color + '22', color: ag.color }}>{ag.code}</span>
                )}
              </div>
              <span className="text-xs text-gray-400 flex-shrink-0">
                {contract.numeroDocument ? `N°${contract.numeroDocument}` : `#${contract.id}`}
              </span>
            </div>
            {/* Ligne 2 : modèle */}
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-xs text-gray-500 truncate">{contract.vehicleName || '—'}</p>
              {usePendingMrcStore.getState().pendingReturns.some(r => String(r.id) === String(contract.rawId ?? contract.id)) && (
                <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide flex-shrink-0">
                  ⚠️ Action MyRentCar
                </span>
              )}
            </div>
            {/* Ligne 3 : client */}
            <p className="text-sm font-semibold text-gray-700 truncate mt-0.5">{contract.clientName}</p>
            {/* Ligne 4 : dates */}
            {(startFmt || endFmt) ? (
              <div className="flex items-center gap-1.5 mt-1.5 bg-gray-50 rounded-lg px-2 py-1.5">
                <Calendar size={12} className="text-gray-400 flex-shrink-0"/>
                <div className="flex items-center gap-1 text-xs flex-wrap">
                  {startFmt && <span className="text-green-700 font-semibold whitespace-nowrap">↗ {startFmt}</span>}
                  {startFmt && endFmt && <ArrowRight size={10} className="text-gray-300 flex-shrink-0"/>}
                  {endFmt   && <span className="text-orange-600 font-semibold whitespace-nowrap">↙ {endFmt}</span>}
                </div>
              </div>
            ) : (
              <div className="mt-1.5 flex flex-col gap-1.5 w-2/3">
                <div className="h-2 bg-gray-200 animate-pulse rounded-full w-full"></div>
                <div className="h-2 bg-gray-200 animate-pulse rounded-full w-3/4"></div>
              </div>
            )}
          </div>
          <ChevronRight size={16} className="text-gray-300 flex-shrink-0"/>
        </div>
      </Card>
    </button>
  )
}